<section class="page-offset section-pad">
  <div class="container">
    <h1 class="fw-bold mb-4">{{ i18n.t('settings.title') }}</h1>
    <div class="soft-card bg-white p-4">
      <label class="form-label fw-bold" for="settingsLanguage">{{ i18n.t('settings.language.label') }}</label>
      <select id="settingsLanguage" class="form-select mb-2" [ngModel]="i18n.currentLanguage()" (ngModelChange)="setLanguage($event)">
        @for (language of i18n.supportedLanguages; track language.code) {
          <option [ngValue]="language.code">{{ i18n.t(language.labelKey) }}</option>
        }
      </select>
      <p class="text-secondary small mb-4">{{ i18n.t('settings.language.help') }}</p>

      <label class="form-label fw-bold" for="settingsDateFormat">{{ i18n.t('settings.dateFormat.label') }}</label>
      <select id="settingsDateFormat" class="form-select mb-2" [ngModel]="i18n.currentDateFormat()" (ngModelChange)="setDateFormat($event)">
        @for (format of i18n.supportedDateFormats; track format) {
          <option [ngValue]="format">{{ format }}</option>
        }
      </select>
      <p class="text-secondary small mb-4">{{ i18n.t('settings.dateFormat.help') }}</p>

      <div class="form-check form-switch mb-3">
        <input id="settingsDarkTheme" class="form-check-input" type="checkbox" [ngModel]="theme.currentTheme() === 'dark'" (ngModelChange)="setDarkTheme($event)">
        <label class="form-check-label" for="settingsDarkTheme">{{ i18n.t('settings.theme.dark') }}</label>
      </div>
      <div class="border rounded-3 p-3 mb-3">
        <div class="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center">
          <div>
            <h2 class="h5 mb-1">{{ i18n.t('settings.billing.title') }}</h2>
            <p class="text-secondary small mb-0">{{ i18n.t('settings.billing.description') }}</p>
          </div>
          <a class="btn btn-outline-primary" routerLink="/plans">{{ i18n.t('settings.billing.viewPlans') }}</a>
        </div>
        @if (isLoadingBilling) {
          <div class="py-4" aria-live="polite">
            <app-loading-spinner [label]="i18n.t('settings.billing.loading')"></app-loading-spinner>
          </div>
        } @else {
          <div class="billing-summary row g-2 align-items-center mt-3">
            <div class="col-sm-4 col-lg-3 text-secondary small">{{ i18n.t('settings.billing.currentPlan') }}&nbsp;:</div>
            <div class="col-sm-8 col-lg-9 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
              <strong>{{ currentPlanLabel }}</strong>
              <button class="btn btn-primary" type="button" (click)="openBillingPortal()" [disabled]="isOpeningBillingPortal || !canManageBilling" [attr.aria-busy]="isOpeningBillingPortal">
                @if (isOpeningBillingPortal) { <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span> }
                {{ isOpeningBillingPortal ? i18n.t('plans.openingPortal') : i18n.t('plans.manageBilling') }}
              </button>
            </div>
          </div>
          @if (billingStatusKey) {
            <div class="alert alert-warning mt-3 mb-0" role="status">{{ i18n.t(billingStatusKey) }}</div>
          }
          <div class="d-flex flex-wrap gap-2 mt-2">
            @if (!canManageBilling) {
              <span class="text-secondary small align-self-center">{{ i18n.t('settings.billing.manageUnavailable') }}</span>
            }
          </div>
        }
      </div>
      @if (isLoadingPreferences) {
        <div class="py-5" aria-live="polite">
          <app-loading-spinner [label]="i18n.t('common.loadingPage')"></app-loading-spinner>
        </div>
      } @else {
      <h2 class="h5 mt-4">{{ i18n.t('settings.newsletterSettings') }}</h2>
      <p class="text-secondary small">{{ i18n.t('settings.newsletterHelp') }}</p>
      @if (preferencesStatusKey) {
        <div class="alert" [class.alert-success]="preferencesSuccessStatusKeys.has(preferencesStatusKey)" [class.alert-warning]="!preferencesSuccessStatusKeys.has(preferencesStatusKey)" role="status">
          {{ i18n.t(preferencesStatusKey) }}
        </div>
      }
      <div class="border rounded-3 p-3 mb-3">
        <h3 class="h6 mb-1">{{ i18n.t('settings.categories.offers.title') }}</h3>
        <p class="text-secondary small mb-2">{{ i18n.t('settings.categories.offers.help') }}</p>
        <p class="text-secondary small mb-3">{{ i18n.t('settings.securityNotificationsMandatory') }}</p>
        <label class="form-label fw-bold" for="settingsDigest">{{ i18n.t('settings.digest.label') }}</label>
        <select id="settingsDigest" class="form-select mb-2" [ngModel]="preferences.digest" (ngModelChange)="setDigest($event)" [disabled]="areNotificationSettingsDisabled">
          @for (digest of digestOptions; track digest) {
            <option [ngValue]="digest">{{ i18n.t('settings.digest.' + digest) }}</option>
          }
        </select>
        <p class="text-secondary small mb-3">{{ i18n.t('settings.digest.help') }}</p>
        <div class="row g-2 mb-3">
          @for (channel of offerNotificationSettings; track channel.key) {
            <div class="col-md-6">
              <div class="form-check form-switch">
                <input [id]="'settingsNotification' + channel.key" class="form-check-input" type="checkbox" [checked]="isNotificationEnabled(channel.preferenceKey)" [disabled]="isNotificationToggleDisabled(channel)" (change)="setNotificationSetting(channel.preferenceKey, $any($event.target).checked)">
                <label class="form-check-label" [for]="'settingsNotification' + channel.key">{{ i18n.t('settings.notifications.' + channel.key) }}</label>
                @if (channel.proOnly && !isProPlan) {
                  <div class="form-text">{{ i18n.t('settings.destinations.whatsappProOnly') }}</div>
                }
              </div>
            </div>
          }
        </div>
        @if (preferences.sms || preferences.whatsapp) {
          <p class="required-fields-notice text-secondary small bg-light rounded-3 p-3 mb-3">{{ i18n.t('common.requiredFieldsNotice') }}</p>
          <div class="row g-2 mb-2">
            <div class="col-md-5">
              <label class="form-label fw-bold" for="settingsSmsCountry">{{ i18n.t('settings.destinations.smsCountry') }}<span class="required-star" aria-hidden="true">*</span></label>
              <input id="settingsSmsCountrySearch" class="form-control mb-2" type="search" [value]="smsCountrySearch" (keyup)="setSmsCountrySearch($any($event.target).value)" [disabled]="areNotificationSettingsDisabled" [placeholder]="i18n.t('settings.destinations.smsCountrySearchPlaceholder')" [attr.aria-label]="i18n.t('settings.destinations.smsCountrySearch')">
              <select id="settingsSmsCountry" class="form-select" [ngModel]="preferences.smsCountryCode ?? 'FR'" (ngModelChange)="setSmsCountry($event)" [required]="preferences.sms || preferences.whatsapp" [disabled]="areNotificationSettingsDisabled">
                @for (country of filteredSmsCountryOptions(); track country.code) {
                  <option [ngValue]="country.code">{{ country.name }} ({{ country.dialCode }})</option>
                }
              </select>
              @if (filteredSmsCountryOptions().length === 0) {
                <p class="text-secondary small mt-2 mb-0">{{ i18n.t('settings.destinations.smsCountryNoResults') }}</p>
              }
            </div>
            @if (preferences.sms) {
              <div class="col-md-7">
                <label class="form-label fw-bold" for="settingsSmsPhone">{{ i18n.t('settings.destinations.smsPhoneNumber') }}<span class="required-star" aria-hidden="true">*</span></label>
                <div class="input-group">
                  <span class="input-group-text">{{ smsDialCode() }}</span>
                  <input id="settingsSmsPhone" class="form-control" type="tel" [ngModel]="preferences.smsPhoneNumber ?? ''" (ngModelChange)="setNotificationTextPreference('smsPhoneNumber', $event)" [required]="preferences.sms" [class.is-invalid]="smsValidationErrorKey" [disabled]="areNotificationSettingsDisabled" [placeholder]="smsPlaceholder()">
                </div>
              </div>
            }
          </div>
          @if (smsValidationErrorKey) {
            <div class="invalid-feedback d-block mb-2">{{ i18n.t(smsValidationErrorKey) }}</div>
          }
          <p class="text-secondary small mb-3">{{ i18n.t('settings.destinations.smsHelp') }}</p>
          @if (preferences.smsVerificationRequired && !preferences.smsPhoneNumberVerified) {
            <div class="border rounded-3 p-3 mb-3 bg-light">
              <label class="form-label fw-bold" for="settingsSmsVerificationCode">{{ i18n.t('settings.destinations.smsVerificationCode') }}</label>
              <div class="input-group mb-2">
                <input id="settingsSmsVerificationCode" class="form-control" type="text" inputmode="numeric" autocomplete="one-time-code" [value]="smsVerificationCode" (input)="setSmsVerificationCode($any($event.target).value)" [disabled]="areNotificationSettingsDisabled" [placeholder]="i18n.t('settings.destinations.smsVerificationCodePlaceholder')">
                <button class="btn btn-outline-primary" type="button" (click)="confirmSmsVerification()" [disabled]="areNotificationSettingsDisabled || !smsVerificationCode.trim()">{{ i18n.t('settings.destinations.confirmSmsVerification') }}</button>
                <button class="btn btn-outline-secondary" type="button" (click)="resendSmsVerificationCode()" [disabled]="areNotificationSettingsDisabled || !preferences.smsPhoneNumber || smsVerificationCooldownSeconds > 0">
                  @if (smsVerificationCooldownSeconds > 0) {
                    {{ i18n.t('settings.destinations.resendSmsVerificationCooldown', { seconds: smsVerificationCooldownSeconds }) }}
                  } @else {
                    {{ i18n.t('settings.destinations.resendSmsVerification') }}
                  }
                </button>
              </div>
              <p class="text-secondary small mb-0">{{ i18n.t('settings.destinations.smsVerificationHelp') }}</p>
            </div>
          }
        }
        @if (preferences.whatsapp) {
          <label class="form-label fw-bold" for="settingsWhatsappPhoneNumber">{{ i18n.t('settings.destinations.whatsappPhoneNumber') }}</label>
          <div class="input-group mb-2">
            <span class="input-group-text">{{ smsDialCode() }}</span>
            <input id="settingsWhatsappPhoneNumber" class="form-control" type="tel" [ngModel]="preferences.whatsappPhoneNumber ?? ''" (ngModelChange)="setNotificationTextPreference('whatsappPhoneNumber', $event)" [disabled]="areNotificationSettingsDisabled" [placeholder]="smsPlaceholder()" autocomplete="tel" inputmode="tel">
          </div>
          @if (whatsappValidationErrorKey) {
            <div class="alert alert-warning py-2 mb-2" role="alert">{{ i18n.t(whatsappValidationErrorKey) }}</div>
          }
          <p class="text-secondary small mb-3">{{ i18n.t('settings.destinations.whatsappHelp') }}</p>
        }
        @if (preferences.discord) {
          <label class="form-label fw-bold" for="settingsDiscordUsername">{{ i18n.t('settings.destinations.discordUsername') }}</label>
          <div class="input-group mb-2">
            <input id="settingsDiscordUsername" class="form-control" [type]="isDiscordWebhookUrlVisible ? 'url' : 'password'" [ngModel]="preferences.discordUsername ?? ''" (ngModelChange)="setNotificationTextPreference('discordUsername', $event)" [disabled]="areNotificationSettingsDisabled" placeholder="https://discord.com/api/webhooks/..." autocomplete="off" autocapitalize="off" spellcheck="false">
            <button class="btn btn-outline-secondary" type="button" (click)="toggleDiscordWebhookVisibility()" [attr.aria-label]="i18n.t(isDiscordWebhookUrlVisible ? 'settings.destinations.hideDiscordWebhook' : 'settings.destinations.showDiscordWebhook')" [disabled]="areNotificationSettingsDisabled">
              {{ i18n.t(isDiscordWebhookUrlVisible ? 'settings.destinations.hideDiscordWebhook' : 'settings.destinations.showDiscordWebhook') }}
            </button>
            <button class="btn btn-outline-primary" type="button" (click)="testDiscordWebhook()" [disabled]="areNotificationSettingsDisabled || isTestingDiscordWebhook" [attr.aria-busy]="isTestingDiscordWebhook">
              @if (isTestingDiscordWebhook) { <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span> }
              {{ i18n.t('settings.destinations.testDiscordWebhook') }}
            </button>
            <button class="btn btn-outline-primary" type="button" (click)="openDiscordServerSetup()" [disabled]="areNotificationSettingsDisabled">{{ i18n.t('settings.destinations.addDiscordServer') }}</button>
          </div>
          @if (discordValidationErrorKey) {
            <div class="alert alert-warning py-2 mb-2" role="alert">{{ i18n.t(discordValidationErrorKey) }}</div>
          }
          <p class="text-secondary small mb-3">{{ i18n.t('settings.destinations.discordHelp') }}</p>
          <label class="form-label fw-bold" for="settingsDiscordMessagePrefix">{{ i18n.t('settings.destinations.discordMessagePrefix') }}</label>
          <input id="settingsDiscordMessagePrefix" class="form-control mb-2" type="text" [ngModel]="preferences.discordMessagePrefix ?? ''" (ngModelChange)="setNotificationTextPreference('discordMessagePrefix', $event)" [disabled]="areNotificationSettingsDisabled" [placeholder]="i18n.t('settings.destinations.discordMessagePrefixPlaceholder')" maxlength="200" autocomplete="off" spellcheck="false">
          <p class="text-secondary small mb-3">{{ i18n.t('settings.destinations.discordMessagePrefixHelp') }}</p>
        }
        <button class="btn btn-primary mb-3" type="button" (click)="saveNotificationPanel()" [disabled]="areNotificationSettingsDisabled" [attr.aria-busy]="isSavingPreferences">
          @if (isSavingPreferences) { <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span> }
          {{ i18n.t('settings.saveNotifications') }}
        </button>
      </div>
      <div class="d-flex flex-wrap gap-2 mb-3">
        <button class="btn btn-outline-secondary" type="button" (click)="toggleNotificationSubscription()" [disabled]="isSavingPreferences || preferences.stopProcessing" [attr.aria-busy]="isSavingPreferences">
          @if (isSavingPreferences) { <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span> }
          {{ i18n.t(notificationSubscriptionActionKey) }}
        </button>
        <button class="btn" [class.btn-outline-warning]="!preferences.stopProcessing" [class.btn-outline-success]="preferences.stopProcessing" type="button" (click)="toggleProcessingObjection()" [disabled]="isSavingPreferences" [attr.aria-busy]="isSavingPreferences">
          {{ i18n.t(processingObjectionActionKey) }}
        </button>
      </div>
      @if (hasPreferenceEvidence) {
        <div class="border rounded-3 p-3 mb-3 bg-light" aria-live="polite">
          <h3 class="h6 mb-2">{{ i18n.t('settings.evidence.title') }}</h3>
          <ul class="list-unstyled small text-secondary mb-0">
            @if (preferences.preferencesUpdatedAt) { <li>{{ i18n.t('settings.evidence.preferencesUpdatedAt') }} {{ preferences.preferencesUpdatedAt }}</li> }
            @if (preferences.unsubscribedAt) { <li>{{ i18n.t('settings.evidence.unsubscribedAt') }} {{ preferences.unsubscribedAt }}</li> }
            @if (preferences.objectedAt) { <li>{{ i18n.t('settings.evidence.objectedAt') }} {{ preferences.objectedAt }}</li> }
            @if (preferences.objectionRevokedAt) { <li>{{ i18n.t('settings.evidence.objectionRevokedAt') }} {{ preferences.objectionRevokedAt }}</li> }
          </ul>
        </div>
      }
      <a class="btn btn-outline-danger" routerLink="/account"><i class="bi bi-trash me-2"></i>{{ i18n.t('settings.removeAccount') }}</a>
      <p class="text-secondary small mt-2 mb-0">{{ i18n.t('settings.removeAccountGuidance') }}</p>
      }
    </div>
  </div>
</section>
